import moment from 'moment';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { getCurrncySymbol } from './common-utils';

let store: any;
export const injectStore = (_store: any) => {
  store = _store;
};

moment.locale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    ss: '%d sec',
    m: 'a min',
    mm: '%d mins',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    w: 'a week',
    ww: '%d weeks',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
});

export const dateTimeFormat = (date: string): string => (date && date.length > 0 ? moment(date).format('MMM DD, YYYY. hh:mm A') : ''); // Aug 22, 2022. 12:00 PM
export const dateFormatStandard = (date: string): string => (date && date.length > 0 ? moment(date).format('DD/MM/YYYY') : ''); // 08/08/2022
export const durationFormat = (date: string): string => (date && date.length > 0 ? moment(date).fromNow() : ''); // 2 hours ago
export const timeFormat = (date: string): string => (date && date.length > 0 ? moment(date, 'HH:mm:ss').format('h:mm A') : ''); // 9:45 AM
export const monthDayFormat = (date: string): string => (date && date.length > 0 ? moment(date, 'YYYY-MM-DD').format('MMM Do') : ''); // Aug 22nd
export const dateFormat = (date: string): string => (date && date.length > 0 ? moment(date).format('MMM DD, YYYY') : ''); // Aug 22nd, 2022
export const monthYearFormat = (date: string): string => (date && date.length > 0 ? moment(date).format('MMMM YYYY') : ''); // Auguest 2022
export const dayMonthFormat = (date: string): string => (date && date.length > 0 ? moment(date).format('dddd, Do MMM.') : ''); // Monday, 3rd Sep
export const isoFormat = (date: string, format: string): string => (date && date.length > 0 ? moment(date).format(format) : '');
export const isoTimeFormat = (date: string): string => moment(new Date(date)).format('hh:mm A');
export const isoDateFormat = (date: string): string => moment(new Date(date)).format('MMM DD, YYYY');

function padTo2Digits(num: number) {
  return num.toString().padStart(2, '0');
}

export const toHoursAndMinutes = (totalMinutes: number) => {
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
};

export const secondsToHoursAndMinutes = (totalSeconds:number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}H ${minutes}m`;
};

export const getFileSize = (size: number) => {
  if (size <= 0) { return ''; }

  const bytes = size * 1024 * 1024 * 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / 1024 ** i).toFixed(2))} ${sizes[i]}`;
};

export const amountFormat = (price: string | number) => Number(price).toFixed(2).toLocaleString();
export const decimalFormat = (number: string | number, decimalPoints: number) => (
  Number(number).toFixed(decimalPoints).toLocaleString()
);

export const currencyFormat = async (
  amount: string,
  currency: string,
  convertCurrency: string,
  isUseSymbol: boolean = false,
) => {
  let exRates: any = {};
  const coreState = store.getState()['feature/base/core'];
  if (store && coreState) {
    exRates = coreState.enterprise.exchange_rates;
    if (exRates?.USD !== undefined) {
      // for USD
      let exRate = exRates.USD / exRates[currency.toUpperCase()];
      // for none USD currencies
      if (convertCurrency !== 'USD') {
        exRate *= exRates[convertCurrency.toUpperCase()];
      }
      return `${isUseSymbol ? `${getCurrncySymbol(currency)} ` : ''}${Number(
        (exRate * +amount).toFixed(2),
      )}`;
    }
    return `${isUseSymbol ? `${getCurrncySymbol(currency)} ` : ''}${Number(amount).toFixed(2)}`;
  }
  return `${isUseSymbol ? `${getCurrncySymbol(currency)} ` : ''}${Number(amount).toFixed(2)}`;
};

export const getCurrencySymbol = (currencyType: string) => {
  const currency = currencyType.toUpperCase();
  let symbol = currency;
  switch (currency) {
    case 'USD':
      symbol = '$';
      break;
    case 'AUD':
      symbol = 'A$';
      break;
    case 'CAD':
      symbol = 'C$';
      break;
    case 'GBP':
      symbol = '£';
      break;
    case 'EUR':
      symbol = '€';
      break;
    case 'JPY':
      symbol = '¥';
      break;
    default:
      symbol = currency;
  }
  return symbol;
};

export const getDateToUserTimezone = (
  dateString: string | number,
  formatString?: string,
) => {
  const defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    if (formatString) {
      return formatInTimeZone(dateString, defaultTimeZone, formatString);
    }

    return toDate(dateString, { timeZone: defaultTimeZone });
  } catch (error) {
    return dateString;
  }
};

const linkReplacer = (matched: string) => {
  let withProtocol = matched;
  if (!withProtocol.startsWith('http')) {
    withProtocol = `https://${matched}`;
  }
  const newStr = `<a rel="noreferrer" class="text-link" href="${withProtocol}" target="_blank">${matched}</a>`;
  return newStr;
};

export const replaceLinksByTagsInString = (text: string) => {
  const reg = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
  return text.replace(reg, linkReplacer).replaceAll('\n', '<br>');
};
